import React, { useState, useEffect } from 'react';
import './ClueSortGame.css';
import { getRandomCasePair } from './clueSortData';
import clueConnectionsLogo from '../../images/clue-connections.png';

const ClueCard = ({ clue, onSort, sortedState }) => {
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [baseOffset, setBaseOffset] = useState(0); // Base position before drag starts
  const [isSorting, setIsSorting] = useState(false);
  const [hasMoved, setHasMoved] = useState(false); // Track if there was actual movement
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  // Track screen width changes
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Reset offset when sorted state changes externally
  useEffect(() => {
    // Use a consistent offset for each state, and ensure this happens
    // even after excessive dragging
    let newOffset = 0;
    
    // Adjust offsets based on screen size
    const isSmallScreen = screenWidth < 768;
    const farOffset = isSmallScreen ? 60 : 100;
    const nearOffset = isSmallScreen ? 30 : 50;
    
    // Updated to handle 4 cases
    if (sortedState === 'case1') {
      newOffset = -farOffset;
    } else if (sortedState === 'case2') {
      newOffset = -nearOffset;
    } else if (sortedState === 'case3') {
      newOffset = nearOffset;
    } else if (sortedState === 'case4') {
      newOffset = farOffset;
    }
    
    setOffsetX(newOffset);
    setBaseOffset(newOffset); // Store base position for dragging
  }, [sortedState, screenWidth]);

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsSorting(true);
    setHasMoved(false); // Reset movement detection
    // Don't reset the offset here, maintain current position
  };

  const handleMouseDown = (e) => {
    setStartX(e.clientX);
    setIsSorting(true);
    setHasMoved(false); // Reset movement detection
    // Don't reset the offset here, maintain current position
  };

  const handleTouchMove = (e) => {
    if (!isSorting) return;
    
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    
    // Only prevent default if we're actually dragging horizontally
    if (Math.abs(diff) > 10) {
      e.preventDefault();
      setHasMoved(true); // User has moved enough to consider it a drag
    }
    
    // Apply drag relative to starting position
    // baseOffset is the starting position
    // diff is how far user has dragged from touch start point
    const maxOffset = screenWidth < 768 ? 70 : 120;
    const newOffset = Math.max(-maxOffset, Math.min(maxOffset, baseOffset + diff));
    setOffsetX(newOffset);
  };

  const handleMouseMove = (e) => {
    if (!isSorting) return;
    
    const currentX = e.clientX;
    const diff = currentX - startX;
    
    if (Math.abs(diff) > 5) {
      setHasMoved(true); // User has moved enough to consider it a drag
    }
    
    // Apply drag relative to starting position
    const maxOffset = screenWidth < 768 ? 70 : 120;
    const newOffset = Math.max(-maxOffset, Math.min(maxOffset, baseOffset + diff));
    setOffsetX(newOffset);
  };

  const handleTouchEnd = () => {
    finishSorting();
  };

  const handleMouseUp = () => {
    finishSorting();
  };

  const handleMouseLeave = () => {
    if (isSorting) {
      finishSorting();
    }
  };

  const finishSorting = () => {
    setIsSorting(false);
    
    // Only change sorting state if there was actual movement
    if (hasMoved) {
      // Determine which category based on offset threshold
      let newState;
      let finalOffset;
      
      // Adjust thresholds based on screen size
      const isSmallScreen = screenWidth < 768;
      const farThreshold = isSmallScreen ? 45 : 75;
      const nearThreshold = isSmallScreen ? 15 : 25;
      const farOffset = isSmallScreen ? 60 : 100;
      const nearOffset = isSmallScreen ? 30 : 50;
      
      // Updated thresholds for 4 cases
      if (offsetX < -farThreshold) {
        newState = 'case1'; // Far left - case1 (blue)
        finalOffset = -farOffset;
      } else if (offsetX < -nearThreshold) {
        newState = 'case2'; // Left - case2 (yellow) 
        finalOffset = -nearOffset;
      } else if (offsetX > farThreshold) {
        newState = 'case4'; // Far right - case4 (red)
        finalOffset = farOffset;
      } else if (offsetX > nearThreshold) {
        newState = 'case3'; // Right - case3 (green)
        finalOffset = nearOffset;
      } else {
        newState = 'neutral';
        finalOffset = 0;
      }
      
      setOffsetX(finalOffset);
      setBaseOffset(finalOffset);
      
      // Only trigger sort if state changed
      if (newState !== sortedState) {
        onSort(clue.id, newState);
      }
    } else {
      // If user just clicked (no drag), maintain current position
      setOffsetX(baseOffset);
    }
    
    setHasMoved(false); // Reset for next interaction
  };

  // Determine styles based on sorting state and offset
  const cardStyle = {
    transform: `translateX(${offsetX}px)`,
    transition: isSorting ? 'none' : 'transform 0.3s ease'
  };

  // Determine card class based on sorted state
  const cardClass = `clue-card ${sortedState}`;

  return (
    <div 
      className={cardClass}
      style={cardStyle}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {clue.text}
    </div>
  );
};

const ClueSortGame = () => {
  // State for the game
  const [cases, setCases] = useState([]);
  const [sortedClues, setSortedClues] = useState({
    case1: [],
    case2: [],
    case3: [],
    case4: [],
    neutral: []
  });
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts] = useState(4);
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  const [showCases, setShowCases] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null); // Add feedback message state
  const [showFeedback, setShowFeedback] = useState(false); // Control feedback visibility
  const [caseMappings, setCaseMappings] = useState({
    // Maps case index to category (case1, case2, case3, case4)
    0: null, // case[0] is sorted into which category
    1: null, // case[1] is sorted into which category
    2: null, // case[2] is sorted into which category
    3: null, // case[3] is sorted into which category
  });

  // Initialize the game by loading clues
  useEffect(() => {
    const randomCases = getRandomCasePair();
    setCases(randomCases);
    
    // Combine clues from all four cases without shuffling them
    const allClues = [
      ...randomCases[0].clues,
      ...randomCases[1].clues,
      ...randomCases[2].clues,
      ...randomCases[3].clues
    ];
    
    // Sort clues by their ID
    const sortedClues = allClues.sort((a, b) => a.id - b.id);
    
    setSortedClues({
      case1: [],
      case2: [],
      case3: [],
      case4: [],
      neutral: sortedClues
    });
  }, []);

  // Handle sorting a clue
  const handleSortClue = (clueId, destination) => {
    // Find which section currently contains the clue
    let sourceSection = null;
    for (const section of ['case1', 'case2', 'case3', 'case4', 'neutral']) {
      if (sortedClues[section].some(clue => clue.id === clueId)) {
        sourceSection = section;
        break;
      }
    }

    if (sourceSection && sourceSection !== destination) {
      // Create a copy of the current state
      const updatedSortedClues = { ...sortedClues };
      
      // Find the clue object
      const clueToMove = updatedSortedClues[sourceSection].find(clue => clue.id === clueId);
      
      // Remove from source
      updatedSortedClues[sourceSection] = updatedSortedClues[sourceSection].filter(clue => clue.id !== clueId);
      
      // Add to destination
      updatedSortedClues[destination].push(clueToMove);
      
      // Update state
      setSortedClues(updatedSortedClues);
      
      // Hide feedback when user starts sorting again
      setShowFeedback(false);
    }
  };

  // Get the sorted state for a specific clue
  const getClueState = (clueId) => {
    if (sortedClues.case1.some(clue => clue.id === clueId)) return 'case1';
    if (sortedClues.case2.some(clue => clue.id === clueId)) return 'case2';
    if (sortedClues.case3.some(clue => clue.id === clueId)) return 'case3';
    if (sortedClues.case4.some(clue => clue.id === clueId)) return 'case4';
    return 'neutral';
  };

  // Check if the player's sorting is correct
  const checkSorting = () => {
    let correct = true;
    let incorrectCount = 0;
    
    // First, check if all clues are sorted (none in neutral)
    if (sortedClues.neutral.length > 0) {
      correct = false;
      incorrectCount = sortedClues.neutral.length;
    } else {
      // Get the belongsTo values of the first clue in each case
      const case1FirstClue = sortedClues.case1.length > 0 ? sortedClues.case1[0].belongsTo - 1 : null;
      const case2FirstClue = sortedClues.case2.length > 0 ? sortedClues.case2[0].belongsTo - 1 : null;
      const case3FirstClue = sortedClues.case3.length > 0 ? sortedClues.case3[0].belongsTo - 1 : null;
      const case4FirstClue = sortedClues.case4.length > 0 ? sortedClues.case4[0].belongsTo - 1 : null;
      
      // Update case mappings based on player's sorting
      const newCaseMappings = { ...caseMappings };
      if (case1FirstClue !== null) newCaseMappings[case1FirstClue] = 'case1';
      if (case2FirstClue !== null) newCaseMappings[case2FirstClue] = 'case2';
      if (case3FirstClue !== null) newCaseMappings[case3FirstClue] = 'case3';
      if (case4FirstClue !== null) newCaseMappings[case4FirstClue] = 'case4';
      setCaseMappings(newCaseMappings);
      
      // Count incorrect clues in case1
      for (const clue of sortedClues.case1) {
        if (clue.belongsTo - 1 !== case1FirstClue) {
          correct = false;
          incorrectCount++;
        }
      }
      
      // Count incorrect clues in case2
      for (const clue of sortedClues.case2) {
        if (clue.belongsTo - 1 !== case2FirstClue) {
          correct = false;
          incorrectCount++;
        }
      }
      
      // Count incorrect clues in case3
      for (const clue of sortedClues.case3) {
        if (clue.belongsTo - 1 !== case3FirstClue) {
          correct = false;
          incorrectCount++;
        }
      }
      
      // Count incorrect clues in case4
      for (const clue of sortedClues.case4) {
        if (clue.belongsTo - 1 !== case4FirstClue) {
          correct = false;
          incorrectCount++;
        }
      }
      
      // Check that each non-empty case contains a unique case
      // We need to compare each pair of non-empty cases
      const nonEmptyCases = [];
      const nonEmptyBelongsTo = [];
      
      if (case1FirstClue !== null) {
        nonEmptyCases.push("case1");
        nonEmptyBelongsTo.push(case1FirstClue);
      }
      if (case2FirstClue !== null) {
        nonEmptyCases.push("case2");
        nonEmptyBelongsTo.push(case2FirstClue);
      }
      if (case3FirstClue !== null) {
        nonEmptyCases.push("case3");
        nonEmptyBelongsTo.push(case3FirstClue);
      }
      if (case4FirstClue !== null) {
        nonEmptyCases.push("case4");
        nonEmptyBelongsTo.push(case4FirstClue);
      }
      
      // Check for duplicate belongsTo values
      const uniqueBelongsTo = [...new Set(nonEmptyBelongsTo)];
      if (uniqueBelongsTo.length < nonEmptyBelongsTo.length) {
        // We have duplicate belongsTo values, which means clues from the same case are in different buckets
        correct = false;
        
        // Find which cases have the same belongsTo values
        const duplicateValues = nonEmptyBelongsTo.filter((value, index, self) => 
          self.indexOf(value) !== index || self.lastIndexOf(value) !== index
        );
        
        // Count the number of clues in the duplicate cases (except for the one with the most clues)
        for (const duplicateValue of [...new Set(duplicateValues)]) {
          const casesWithDuplicate = nonEmptyCases.filter((caseId, index) => 
            nonEmptyBelongsTo[index] === duplicateValue
          );
          
          // Get the number of clues in each case
          const casesCounts = casesWithDuplicate.map(caseId => 
            sortedClues[caseId].length
          );
          
          // Add all but the highest count to incorrectCount
          if (casesCounts.length > 1) {
            const sumOfCounts = casesCounts.reduce((sum, count) => sum + count, 0);
            const maxCount = Math.max(...casesCounts);
            incorrectCount += (sumOfCounts - maxCount);
          }
        }
      }
    }

    if (correct) {
      setGameStatus('won');
      setShowCases(true);
      setShowFeedback(false);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      // Set feedback message
      if (incorrectCount === 1) {
        setFeedbackMessage("You are 1 away from solving the mystery!");
      } else {
        setFeedbackMessage(`You are ${incorrectCount} away from solving the mystery!`);
      }
      setShowFeedback(true);
      
      if (newAttempts >= maxAttempts) {
        setGameStatus('lost');
        setShowCases(true);
        setShowFeedback(false);
      }
    }
  };

  // Get all clues in a single array for rendering, preserving the ID order
  const getAllClues = () => {
    const allClues = [
      ...sortedClues.case1,
      ...sortedClues.case2,
      ...sortedClues.case3,
      ...sortedClues.case4,
      ...sortedClues.neutral
    ];
    
    // Sort the clues according to their ID
    return allClues.sort((a, b) => a.id - b.id);
  };

  // Render attempt indicator dots
  const renderAttemptDots = () => {
    const dots = [];
    for (let i = 0; i < maxAttempts; i++) {
      dots.push(
        <div 
          key={i} 
          className={`attempt-dot ${i < maxAttempts - attempts ? 'remaining' : 'used'}`}
        />
      );
    }
    return dots;
  };

  // Get maximum clues per case (number of clues from each original case)
  const getMaxCluesPerCase = () => {
    if (cases.length === 0) return 5; // Default fallback based on updated clue count
    return cases[0].clues.length; // Will dynamically return the actual number of clues in a case
  };

  // Get case category class based on player's sorting choices
  const getCaseCategoryClass = (caseIndex) => {
    return caseMappings[caseIndex] || '';
  };

  // Toggle function for revealing cases
  const toggleCases = () => {
    setShowCases(!showCases);
  };

  if (cases.length === 0) {
    return <div className="loading">Loading cases...</div>;
  }

  return (
    <div className="clue-sort-game">
      <div className="story-title">
        <img src={clueConnectionsLogo} alt="Clue Connections" className="game-logo" />
      </div>
      
      <div className="case-labels">
        <div className="case-label case1-label">
          <h2>Case 1</h2>
          <div className="clue-counter">{sortedClues.case1.length}/{getMaxCluesPerCase()}</div>
        </div>
        <div className="case-label case2-label">
          <h2>Case 2</h2>
          <div className="clue-counter">{sortedClues.case2.length}/{getMaxCluesPerCase()}</div>
        </div>
        <div className="case-label case3-label">
          <h2>Case 3</h2>
          <div className="clue-counter">{sortedClues.case3.length}/{getMaxCluesPerCase()}</div>
        </div>
        <div className="case-label case4-label">
          <h2>Case 4</h2>
          <div className="clue-counter">{sortedClues.case4.length}/{getMaxCluesPerCase()}</div>
        </div>
      </div>
      
      <div className="game-board">
        <div className="case-line case1-line"></div>
        <div className="case-line case2-line"></div>
        <div className="case-line case3-line"></div>
        <div className="case-line case4-line"></div>
        <div className="clues-container">
          {getAllClues().map(clue => (
            <ClueCard 
              key={clue.id}
              clue={clue}
              onSort={handleSortClue}
              sortedState={getClueState(clue.id)}
            />
          ))}
        </div>
      </div>
      
      {showFeedback && (
        <div className="feedback-message">
          <div className="feedback-icon">!</div>
          <p>{feedbackMessage}</p>
        </div>
      )}
      
      <div className="attempts-indicator">
        {renderAttemptDots()}
      </div>
      
      <div className="game-controls">
        <button 
          className="guess-button"
          onClick={checkSorting}
          disabled={gameStatus !== 'playing' || sortedClues.neutral.length > 0}
        >
          Submit Guess
        </button>
        <button 
          className="reveal-button"
          onClick={toggleCases}
        >
          {showCases ? 'Hide Cases' : 'Reveal Cases'}
        </button>
      </div>
      
      {showCases && (
        <div className="cases-reveal">
          {cases.map((caseData, index) => (
            <div key={index} className={`case-reveal ${getCaseCategoryClass(index)}`}>
              <div className="case-reveal-header">
                <span className="case-number">Case {index + 1}</span>
                <h3>{caseData.title}</h3>
              </div>
              <div className="case-reveal-content">
                <p>{caseData.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {gameStatus !== 'playing' && (
        <div className={`game-result ${gameStatus}`}>
          <h2>{gameStatus === 'won' ? 'You Won!' : 'Game Over'}</h2>
          <p>{gameStatus === 'won' 
            ? 'Congratulations! You correctly sorted all the clues.' 
            : 'You ran out of attempts. Better luck next time!'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ClueSortGame; 